// Layer 3 (Transformations): the Metallurgist "Lab data (LIMS)" 12h
// composite table — map-role-dashboards.md §4b. Rows are the last six
// shift composites from the seed series; SPC state is computed from the
// SPC engine at each stamp (docs/engines/spc.md).

import type { SessionState } from '@/versions/v1/schema/scenario';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { spcRead } from '@/versions/v1/shared/SpcEngine';
import { readingsAt } from '@/versions/v1/shared/series.derive';
import { smelterPenaltyLimit } from '@/versions/v1/shared/bunker-hill-smelter-terms';
import {
  formatCompositeLabel,
  lastCompositeStamps,
  shiftValueAt,
} from './LandingCursor.transform';

/** Preserve the LIMS tile's 2.4% watch onset as 80% of its 3.0% limit.
 * This is a watch band, not a second operating target. */
export function pbInZnTone(value: number): 'ok' | 'warn' | 'bad' {
  const limit = smelterPenaltyLimit('Zn', 'Pb');
  return value > limit ? 'bad' : value * 100 >= 80 * limit ? 'warn' : 'ok';
}

/** K20 (#717): the Pb/Ag concentrate Ag grade's own precision — 2 dp, the
 *  precision `rd-kpi-ag-grade` carries now that its live value is the day
 *  balance's 2 dp product grade (12.14 oz/t). Every Ag figure the landing
 *  prints formats here rather than at a local `.toFixed(1)`, so the LIMS
 *  register, the KPI caption and the balance state one grade at one
 *  precision. */
export function agGradeDecimals(): number {
  return readingByIdLookup('rd-kpi-ag-grade')?.decimals ?? 2;
}

export type LimsRow = {
  id: string;
  composite: string;
  pbGrade: string;
  znGrade: string;
  pbInZn: string;
  pbInZnTone: ReturnType<typeof pbInZnTone>;
  ag: string;
  spcState: 'In control' | 'Watch';
  spcPillTone: 'success' | 'warning';
  aiFlag?: string;
};

const AI_FLAGS = {
  high_pb_in_zn: 'Pb in Zn con approaching upper limit — SMBS ratio review recommended',
  p80_coarsen: 'P80 coarsening — contributing to Zn recovery decline',
  baseline: 'Baseline — all grades near target',
} as const;

/**
 * The under-activation story attaches to the batch-strength reading
 * (`rd-cuso4-batch-strength`), never to the dose-rate series — the dose rate
 * (`rs-reag-cuso4`) is rising, so a dose-decay flag would contradict the
 * cross-area row on the neighbouring tab (decisions.md § Landings CuSO4).
 */
function cuso4BatchFlag(state: SessionState): string {
  const projected = readingsAt(state).find((r) => r.id === 'rd-cuso4-batch-strength');
  const seed = readingByIdLookup('rd-cuso4-batch-strength');
  const value = projected?.value ?? seed?.value ?? 0;
  const design = projected?.design ?? seed?.design ?? 0;
  return `CuSO₄ batch strength ${value.toFixed(0)} g/L vs ${design.toFixed(0)} g/L minimum — under-activation`;
}

function spcStateAt(stamp: string, state: SessionState): LimsRow['spcState'] {
  const cursorState = { ...state, cursor: stamp as typeof state.cursor };
  const pbInZn = spcRead('mv-spec-zn-pb', cursorState);
  const znGrade = spcRead('mv-zn-grade', cursorState);
  const pbGrade = spcRead('mv-pb-grade', cursorState);
  const outOfControl = [pbInZn, znGrade, pbGrade].some((r) => r.signal?.control === 'out');
  return outOfControl ? 'Watch' : 'In control';
}

function aiFlagForRow(
  pbInZn: number,
  index: number,
  total: number,
  state: SessionState,
): string | undefined {
  if (index === 0 && pbInZnTone(pbInZn) !== 'ok') return AI_FLAGS.high_pb_in_zn;
  if (index === 1) return cuso4BatchFlag(state);
  if (index === 2) return AI_FLAGS.p80_coarsen;
  if (index === total - 1) return AI_FLAGS.baseline;
  return undefined;
}

export function limsTableRows(state: SessionState): LimsRow[] {
  const stamps = lastCompositeStamps(state, 6);
  const cursor = state.cursor;
  return stamps.map((stamp, idx) => {
    const pbInZn = shiftValueAt('rs-spec-zn-pb', stamp, state) ?? 0;
    const isLatest = idx === 0;
    const spcState = spcStateAt(stamp, state);
    return {
      id: `lims-${idx + 1}`,
      composite: formatCompositeLabel(stamp, cursor, isLatest),
      pbGrade: (shiftValueAt('rs-pb-grade', stamp, state) ?? 0).toFixed(1),
      znGrade: (shiftValueAt('rs-zn-grade', stamp, state) ?? 0).toFixed(1),
      pbInZn: pbInZn.toFixed(1),
      pbInZnTone: pbInZnTone(pbInZn),
      ag: (shiftValueAt('rs-ag-grade', stamp, state) ?? 0).toFixed(agGradeDecimals()),
      spcState,
      spcPillTone: spcState === 'Watch' ? 'warning' : 'success',
      aiFlag: aiFlagForRow(pbInZn, idx, stamps.length, state),
    };
  });
}
